import {Component, OnDestroy, OnInit} from '@angular/core';
import {ProductionJob} from '../production-job';
import {ActivatedRoute} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {BehaviorSubject, Subject, Subscription} from 'rxjs';
import {PotProductionJobService} from '../potting-production-job.service';
import {CreateStockDialogComponent} from './create-stock-dialog/create-stock-dialog.component';
import {LocationService} from '../../../_services/location.service';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {WithScrollContainer} from '../../../_helpers/with-scroll-container';
import {SelectedLocation} from '../../../common/location-selector/locations';
import {ConfirmDeleteDialogComponent} from './confirm-delete-dialog/confirm-delete-dialog.component';
import {filterProductionJobs} from '../production-job-filter';

@Component({
  selector: 'app-pot-jobs',
  templateUrl: './pot-jobs.component.html',
  styleUrls: ['./pot-jobs.component.scss']
})
export class PotJobsComponent extends WithScrollContainer() implements OnInit, OnDestroy {

  private readonly onDestroy$ = new Subject<void>();
  fromDate: string;
  untilDate: string;
  sortKeyword: string;
  searchTermSubject = new BehaviorSubject<string>('');
  pottingJobs: ProductionJob[] = [];
  filteredPottingJobs: ProductionJob[] = [];
  pottingJobsToShow: ProductionJob[] = [];
  busyFetching = new BehaviorSubject<boolean>(false);
  noJobsFoundMessage: string;
  subscriptions = new Subscription();
  firstVisibleProdutionJobIndex: number;
  private readonly AMOUNT_PRODUCTION_JOBS_VISIBLE = 100;

  constructor(
    private potProductionJobService: PotProductionJobService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private locationService: LocationService
  ) {
    super();
    this.firstVisibleProdutionJobIndex = 0;
  }

  ngOnInit(): void {
    this.refreshData();
    this.subscriptions.add(this.route.data.subscribe(data => {
      this.openDialogWhenIdProvided(data.productionJob);
    }));
    this.subscriptions.add(this.locationService.observeSelectedLocation().subscribe(() => {
      this.refreshTable();
      this.syncVisiblePottingJobsWithAllPottingJobs();
    }));
    this.setupSearchFieldBehaviour();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  async fetchAllProductionJobsForTimePeriod() {
    this.busyFetching.next(true);
    try {
      this.pottingJobs = await this.potProductionJobService.getAllProductionJobsForWeek(this.fromDate, this.untilDate).toPromise();
      this.setupJobsAvailableMessage();
    } catch (error) {
      console.error('Unable to get all production jobs: ' + error);
    }
    this.busyFetching.next(false);
  }

  private refreshTable() {
    this.firstVisibleProdutionJobIndex = 0;
    this.filteredPottingJobs = this.pottingJobs;
    if (this.sortKeyword !== undefined && this.sortKeyword.length > 0) {
      this.applyFilter(this.sortKeyword);
    }
    this.filteredPottingJobs = this.filteredPottingJobs
      .sort((a: ProductionJob, b: ProductionJob) => {
        if (a.priority && !b.priority) {
          return -1;
        } else if (!a.priority && b.priority) {
          return 1;
        } else {
          return a.plantName.localeCompare(b.plantName);
        }
      })
      .filter(item => {
        const selectedLocation = this.locationService.getSelectedLocation().toLowerCase();
        const deliveryLocation = this.locationService.getDeliveryLocationText(item.productionLocation).toLowerCase();

        return selectedLocation === SelectedLocation.All.toLowerCase() ||
          selectedLocation === deliveryLocation;
      });
  }

  async fetchAllProductionJobs() {
    this.busyFetching.next(true);
    try {
      this.pottingJobs = await this.potProductionJobService.getAllProductionJobs().toPromise();
      this.setupJobsAvailableMessage();
    } catch (error) {
      console.error('Unable to get all production jobs: ' + error);
    }
    this.busyFetching.next(false);
  }

  setupJobsAvailableMessage() {
    if (this.pottingJobs.length > 0) {
      this.noJobsFoundMessage = undefined;
    } else {
      this.noJobsFoundMessage = 'Geen productie opdrachten gevonden voor de geselecteerde week';
    }
  }

  openCreateStockDialog(potProductionJob: ProductionJob) {
    const dialogRef = this.dialog.open(CreateStockDialogComponent, {
      data: {
        productionJob: potProductionJob,
        allowMarkingAsProduced: true
      }
    });
    dialogRef.afterClosed().subscribe(() => this.refreshData());
  }

  refreshData() {
    if (!this.fromDate || !this.untilDate ||
      this.fromDate.length === 0 || this.untilDate.length === 0) {
      this.fetchAllProductionJobs().then(() => {
        this.refreshTable();
        this.syncVisiblePottingJobsWithAllPottingJobs();
      });
    } else {
      this.fetchAllProductionJobsForTimePeriod().then(() => {
        this.refreshTable();
        this.syncVisiblePottingJobsWithAllPottingJobs();
      });
    }
  }

  private setupSearchFieldBehaviour() {
    this.searchTermSubject.pipe(
      takeUntil(this.onDestroy$),
      debounceTime(1000),
      distinctUntilChanged(),
    ).subscribe(searchTerm => this.applyFilter(searchTerm));
  }

  private applyFilter(term: string) {
    this.pottingJobsToShow = [];
    this.filteredPottingJobs = filterProductionJobs(
      term,
      this.pottingJobs,
      item => this.locationService.isBedNrFromSelectedLocation(item.productionLocation)
    );
    if (this.filteredPottingJobs.length > 0) {
      this.noJobsFoundMessage = undefined;
    } else {
      this.noJobsFoundMessage = 'Geen productie opdracht gevonden voor zoekterm: ' + term;
    }
    this.syncVisiblePottingJobsWithAllPottingJobs();
  }

  syncVisiblePottingJobsWithAllPottingJobs() {
    this.pottingJobsToShow = this.filteredPottingJobs.slice(this.firstVisibleProdutionJobIndex,
      this.firstVisibleProdutionJobIndex + this.AMOUNT_PRODUCTION_JOBS_VISIBLE);
  }

  loadMoreItemsBottom() {
    const productionJobsToAdd: ProductionJob[] = this.filteredPottingJobs
      .slice(this.firstVisibleProdutionJobIndex + this.AMOUNT_PRODUCTION_JOBS_VISIBLE,
        this.firstVisibleProdutionJobIndex + this.AMOUNT_PRODUCTION_JOBS_VISIBLE + 50);
    productionJobsToAdd.forEach(bed => this.pottingJobsToShow.push(bed));
    this.pottingJobsToShow.splice(0, productionJobsToAdd.length);
    this.firstVisibleProdutionJobIndex += productionJobsToAdd.length;
  }

  private openDialogWhenIdProvided(productionJob: ProductionJob) {
    if (productionJob) {
      this.openCreateStockDialog(productionJob);
    }
  }

  deletePottingProductionJob(pottingProductionJob: ProductionJob) {
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      data: { productionJob: pottingProductionJob}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.potProductionJobService.deleteProductionJob(pottingProductionJob)
          .subscribe(() => {
            this.refreshData();
          });
      }
    });
  }
}
